'use client';

import { Suspense, useEffect, useState } from 'react';

import Image from 'next/image';
import { useSearchParams } from 'next/navigation';

import { ArrowRight, Search } from 'lucide-react';

import { FaqSection } from '@/components/sections/faq-section';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { HELPDESK_LOGOS, getAllTemplates } from '@/lib/template-data';
import { localizeTemplate } from '@/lib/templates/ar';
import { cn } from '@/lib/utils';

// The 7 "high-volume keyword" templates below have no entry in the `t.templates`
// dictionary, so `getAllTemplates()` names them with the English key in BOTH
// locales. Section membership is matched on `template.name`, so these keys must
// stay English here or the cards silently drop out of /ar. Their Arabic copy
// comes from the per-slug overlay via `localizeTemplate()` at render time.
const EXTRA_TEMPLATE_NAMES = {
  emailReplies: 'Automate customer email replies with AI',
  chatbotStarter: 'AI chatbot template starter',
  zendeskMacros: 'Automate Zendesk macros with AI',
  languageDetection: 'Automate language detection on tickets with AI',
  piiRedaction: 'Automate PII redaction on tickets with AI',
  ticketTagging: 'Automate ticket tagging and labeling with AI',
  voicemail: 'Automate voicemail transcription and callback with AI',
} as const;

// English strips the redundant " with AI" tail from every card title; Arabic
// names carry the same tail as "… بالذكاء الاصطناعي". Strip it only when it is
// trailing — several Arabic names use the phrase mid-string.
const stripAiSuffix = (name: string) =>
  name.replace(/ with AI/g, '').replace(/\s*بالذكاء الاصطناعي\s*$/, '');

const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
const toArabicDigits = (value: string) =>
  value.replace(/\d/g, (d) => AR_DIGITS[Number(d)]);

function TemplatesContent() {
  const { t, locale } = useTranslation();
  const xt = (key: keyof typeof EXTRA_TEMPLATE_NAMES) =>
    EXTRA_TEMPLATE_NAMES[key];
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const searchParams = useSearchParams();

  const TEMPLATES = getAllTemplates(t.templates);

  useEffect(() => {
    const systemParam = searchParams.get('system');
    if (systemParam) {
      setSelectedSystem(systemParam);
    }
  }, [searchParams]);

  const templateSections = [
    {
      id: 'support-excellence',
      title: t.templates['Support Excellence'],
      description: t.templates['Elevate your support quality and efficiency'],
      templates: [
        t.templates['Automate support tickets quality assurance with AI'],
        t.templates['Automate skill-based routing with AI'],
        t.templates['Automate escalating to the right team with AI'],
        t.templates[
          'Automate ticket prioritization based on its content with AI'
        ],
        t.templates['Automate ticket sentiment analysis with AI'],
        t.templates[
          'Automate Slack alerts based on the customer issue or report with AI'
        ],
        t.templates[
          'Automate following up with a customer using voice calls with AI'
        ],
        t.templates['Automate phone calling from email with AI'],
        // New: high-volume keyword targets
        xt('emailReplies'),
        xt('chatbotStarter'),
        xt('zendeskMacros'),
        xt('languageDetection'),
        xt('piiRedaction'),
        xt('ticketTagging'),
      ],
    },
    {
      id: 'revenue-automation',
      title: t.templates['Revenue Automation'],
      description: t.templates['Turn support into revenue opportunities'],
      templates: [
        t.templates['Automate up-sells from support tickets with AI'],
        t.templates['Automate cart recovery with AI'],
        t.templates['Automate pre-sales qualification with AI'],
        t.templates['Automate replying to Trustpilot reviews with AI'],
        t.templates[
          'Automate requesting review only when the customer is happy with AI'
        ],
      ],
    },
    {
      id: 'revenue-intelligence',
      title: t.templates['Revenue Intelligence'],
      description:
        t.templates['AI detects opportunities and triggers revenue actions'],
      templates: [
        t.templates['Detect churn risk + proactive retention call'],
        t.templates['Identify high-value lead + priority sales routing'],
        t.templates['Detect expansion opportunity + account growth call'],
        t.templates['Identify payment failure + recovery call'],
        t.templates['Detect usage milestone + upgrade trigger'],
        t.templates['Identify referral opportunity + request referral'],
        t.templates['Detect competitor evaluation + competitive win campaign'],
        t.templates['Identify free trial ending + conversion call'],
        t.templates['Detect bulk purchase intent + enterprise sales handoff'],
        t.templates['Identify feature gap + product-led upsell'],
      ],
    },
    {
      id: 'platform-integration',
      title: t.templates['Platform Integration'],
      description: t.templates['System-specific automations for your helpdesk'],
      templates: [
        t.templates['Automate Zendesk custom fields fill up with AI'],
        t.templates['Automate HubSpot custom fields fill up with AI'],
        t.templates['Automate Salesforce custom fields fill up with AI'],
        t.templates['Automate Intercom custom fields fill up with AI'],
        t.templates['Automate Freshdesk custom fields fill up with AI'],
        t.templates['Automate Twilio Flex custom fields fill up with AI'],
        t.templates['Automate Open custom fields fill up with AI'],
      ],
    },
    {
      id: 'ai-phone-calling',
      title: t.templates['AI Phone Calling'],
      description: t.templates['Automate calls humans typically make'],
      templates: [
        t.templates['AI phone calls for appointment reminders'],
        t.templates['AI phone calls for payment collection'],
        t.templates['AI phone calls for delivery notifications'],
        t.templates['AI phone calls for customer surveys'],
        t.templates['AI phone calls for renewal reminders'],
        t.templates['AI phone calls for win-back campaigns'],
        t.templates['AI phone calls for order confirmations'],
        t.templates['AI phone calls for proactive outreach campaigns'],
        // New: high-volume keyword target
        xt('voicemail'),
      ],
    },
  ];

  const getFilteredTemplates = (templateNames?: string[]) => {
    // Membership is matched on the dictionary-localized name, then each card is
    // localized again through the per-slug Arabic overlay so the index title,
    // description and workflow labels match the detail page exactly.
    const templatesToFilter = (
      templateNames
        ? TEMPLATES.filter((t) => templateNames.includes(t.name))
        : TEMPLATES
    ).map((template) => localizeTemplate(template, locale));

    return templatesToFilter.filter((template) => {
      const matchesSearch = searchQuery
        ? template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          template.description.toLowerCase().includes(searchQuery.toLowerCase())
        : true;
      const matchesSystem = selectedSystem
        ? template.supportedSystems.some(
            (system) => system.name === selectedSystem,
          )
        : true;
      return matchesSearch && matchesSystem;
    });
  };

  return (
    <>
      {/* Hero Section */}
      <section className="bg-[#fafaf9] pt-28 pb-16 md:pt-36 md:pb-20">
        <div className="container">
          <div className="mx-auto max-w-4xl text-center">
            <p className="mb-4 text-[11px] font-medium uppercase ltr:tracking-[0.16em] text-neutral-500">
              {locale === 'ar' ? 'القوالب' : 'Templates'}
            </p>
            <h1 className="font-serif text-balance text-neutral-900 text-[2rem] md:text-4xl xl:text-5xl">
              {t.templates['Automate everything']}
              <sup className="align-super text-[0.4em] ltr:ml-0.5 rtl:mr-0.5">
                ®
              </sup>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {
                t.templates[
                  'Open is the only AI customer communication engine that does it all, very well.'
                ]
              }
            </p>
            <div className="mx-auto mt-10 max-w-3xl">
              <div className="relative">
                <Search className="absolute top-1/2 -translate-y-1/2 ltr:left-4 rtl:right-4 h-4 w-4 text-neutral-400" />
                <Input
                  type="text"
                  placeholder={t.templates['Search templates...']}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-12 rounded-full border-neutral-200 bg-white text-sm shadow-none focus-visible:border-neutral-900 focus-visible:ring-1 focus-visible:ring-neutral-900 md:text-base ltr:pr-4 ltr:pl-11 rtl:pl-4 rtl:pr-11"
                />
              </div>

              {/* Ticketing System Filter */}
              <div className="mt-8 text-center">
                <p className="mb-4 text-xs text-neutral-500">
                  {
                    t.templates[
                      'Filter available templates/automations by ticketing system'
                    ]
                  }
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {HELPDESK_LOGOS.map((system) => (
                    <button
                      key={system.name}
                      onClick={() =>
                        setSelectedSystem(
                          selectedSystem === system.name ? null : system.name,
                        )
                      }
                      className={cn(
                        'inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 md:text-sm',
                        selectedSystem === system.name
                          ? 'border-neutral-900 bg-neutral-900 text-white'
                          : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:text-neutral-900',
                      )}
                    >
                      <div className="relative h-4 w-4 shrink-0 overflow-hidden">
                        <Image
                          src={system.src}
                          alt={system.name}
                          fill
                          className="object-contain"
                        />
                      </div>
                      <span>{system.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Template Sections */}
      {templateSections.map((section, sectionIndex) => {
        const sectionTemplates = getFilteredTemplates(section.templates);

        if (sectionTemplates.length === 0) return null;

        return (
          <section
            key={section.id}
            className={cn(
              'py-20 md:py-24',
              sectionIndex % 2 === 0 ? 'bg-white' : 'bg-[#fafaf9]',
            )}
          >
            <div className="container">
              <div className="mb-10 max-w-2xl md:mb-14">
                <p className="mb-3 text-[10px] font-medium uppercase ltr:tracking-[0.18em] text-neutral-500">
                  {locale === 'ar'
                    ? toArabicDigits(String(sectionIndex + 1).padStart(2, '0'))
                    : String(sectionIndex + 1).padStart(2, '0')}{' '}
                  — {section.title}
                </p>
                <h2 className="font-serif text-balance text-neutral-900 text-2xl md:text-4xl">
                  {section.title}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-neutral-500 md:text-lg">
                  {section.description}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                {sectionTemplates.map((template, index) => (
                  <Link
                    key={index}
                    href={`/templates/${template.slug}`}
                    className="group relative flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white p-6 ltr:text-left rtl:text-right transition-colors duration-200 hover:border-neutral-300 md:p-7"
                  >
                    {/* Title Section */}
                    <div className="mb-5">
                      <h3 className="font-sans text-base text-neutral-900 md:text-lg">
                        {stripAiSuffix(template.name)}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-neutral-500 md:text-base">
                        {template.description}
                      </p>
                    </div>

                    {/* Workflow Visualization */}
                    <div className="mb-5 overflow-x-auto rounded-xl border border-neutral-200 bg-[#fafaf9] p-4 sm:p-5">
                      <div className="flex min-w-max items-center justify-center gap-2 sm:gap-3">
                        {template.workflow.map((step, stepIndex) => {
                          const StepIcon = step.icon;
                          return (
                            <div
                              key={stepIndex}
                              className="flex items-center gap-2 sm:gap-3"
                            >
                              <div className="flex flex-col items-center gap-1.5">
                                <div
                                  className={cn(
                                    'flex h-8 w-8 items-center justify-center rounded-lg ring-1 sm:h-10 sm:w-10',
                                    step.final
                                      ? 'bg-neutral-900 ring-neutral-900'
                                      : 'bg-white ring-neutral-200',
                                  )}
                                >
                                  <StepIcon
                                    className={cn(
                                      'h-4 w-4 sm:h-5 sm:w-5',
                                      step.final
                                        ? 'text-white'
                                        : 'text-neutral-600',
                                    )}
                                  />
                                </div>
                                <span className="max-w-[60px] truncate text-center text-[9px] text-neutral-500 sm:max-w-none sm:text-[10px]">
                                  {step.label}
                                </span>
                              </div>
                              {stepIndex < template.workflow.length - 1 && (
                                <ArrowRight className="h-3 w-3 shrink-0 text-neutral-300 rtl:rotate-180 sm:h-4 sm:w-4" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Supported Systems */}
                    {template.supportedSystems &&
                      template.supportedSystems.length > 0 && (
                        <div className="mt-auto border-t border-neutral-200 pt-5">
                          <p className="mb-3 text-[10px] font-medium uppercase ltr:tracking-[0.16em] text-neutral-500">
                            {t.templates['Supports these ticketing systems']}
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            {template.supportedSystems.map((system) => (
                              <div
                                key={system.name}
                                className="relative h-7 w-7 overflow-hidden rounded-md border border-neutral-200 bg-white"
                              >
                                <Image
                                  src={system.src}
                                  alt={system.name}
                                  fill
                                  className="object-contain p-1"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Show message if no results at all */}
      {templateSections.every(
        (section) => getFilteredTemplates(section.templates).length === 0,
      ) && (
        <section className="bg-white py-20">
          <div className="container">
            <div className="py-20 text-center">
              <p className="text-base leading-relaxed text-neutral-500 md:text-lg">
                {
                  t.templates[
                    'No templates found. Try a different search or system filter.'
                  ]
                }
              </p>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <FaqSection
        eyebrow={locale === 'ar' ? 'الأسئلة الشائعة' : 'FAQ'}
        title={t.templates['Frequently asked questions']}
        subtitle={
          locale === 'ar'
            ? 'أسئلة شائعة عن قوالب Open والتكاملات، وكيف تندمج الأتمتة فعلياً مع الأدوات اللي تستخدمها اليوم.'
            : 'Common questions about Open templates, integrations, and how automation actually fits into your stack.'
        }
        items={[
          {
            question:
              t.templates['How quickly can Open work with my existing setup?'],
            answer:
              t.templates[
                "Open works on top of your existing ticketing system—whether it's Zendesk, Salesforce, HubSpot, Intercom, Freshdesk, or Twilio Flex. Setup takes under one hour. There's no migration, no platform change, no disruption to your team. Simply connect via API and Open starts working within your current helpdesk immediately."
              ],
          },
          {
            question:
              t.templates['What does "automate your support" actually mean?'],
            answer:
              t.templates[
                'Open automates 77% of customer support interactions on average. This means the AI handles the complete resolution of tickets—answering questions, processing requests, updating systems, and closing tickets—without human intervention. For the remaining 23%, it assists your agents or escalates to humans when needed. You choose what percentage to automate.'
              ],
          },
          {
            question: t.templates['How do these workflows and templates work?'],
            answer:
              t.templates[
                'These templates mimic the exact operations your agents perform today—reading tickets, extracting information, making decisions, taking actions, and following up. For example, the "skill-based routing" template reads ticket content just like a human supervisor would, identifies the required expertise, and routes to the right agent. The "quality assurance" template reviews conversations exactly like a QA manager would, scoring quality and identifying coaching opportunities. They replicate human judgment at scale.'
              ],
          },
          {
            question:
              t.templates[
                'Does Open do automation AND workflow orchestration?'
              ],
            answer:
              t.templates[
                'Yes, Open does both. First, it automates direct customer interactions (answering questions, resolving tickets, making calls). Second, it orchestrates complex workflows that involve multiple systems and decision points (routing tickets, triggering alerts, filling fields, escalating issues). You can use pre-built templates for common scenarios or build custom workflows using our agentic workflow engine. Both run simultaneously within your existing systems.'
              ],
          },
          {
            question:
              t.templates[
                'Do I need to change my existing tools or processes?'
              ],
            answer:
              t.templates[
                'No. Open integrates with your existing helpdesk, CRM, phone system, Slack, and other tools. Your agents continue using the same interfaces they know. Your workflows continue running. Open layers on top, working alongside your team without requiring process changes, retraining, or tool migration. It adapts to how you work today.'
              ],
          },
          {
            question:
              t.templates[
                'Can I customize these templates for my specific needs?'
              ],
            answer:
              t.templates[
                'Absolutely. Every template is built using our agentic workflow engine, which means you can customize the logic, add your own rules, integrate with your specific systems, and adjust the automation to match your exact processes. Templates are starting points—you have full control to modify them for your unique requirements.'
              ],
          },
        ]}
        className="border-t border-neutral-200"
      />
    </>
  );
}

export default function TemplatesClient() {
  return (
    <Suspense>
      <TemplatesContent />
    </Suspense>
  );
}
